import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { App } from './App';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuthStore } from './lib/auth';
import { initAnalytics } from '@/lib/analytics';
import { isNativeApp } from '@/lib/platform';
import { applyTheme, getStoredTheme } from '@/lib/theme';
import { installSleeperResumeRefresh } from '@/lib/sleeperRefresh';
import './index.css';

// Apply the user's color theme before first paint (sync localStorage read).
applyTheme(getStoredTheme());

// Foregrounding the app re-pulls Sleeper data (fresh league invites show
// up without a relink). No-op outside the native shell.
installSleeperResumeRefresh();

// Root marker for native-only CSS (e.g. tighter safe-area padding).
if (isNativeApp()) {
  document.documentElement.classList.add('is-native');
  // App shell: pin the viewport scale. Without this, iOS zooms on input
  // focus (any control under 16px) and the zoomed layout pans sideways.
  // Native only - the WEB viewport keeps pinch-zoom for accessibility.
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );
}

// Load GA4 (shared property, cross-domain linker) as early as possible. No-ops
// off the live web hosts, so dev + the native app stay out of the property.
initAnalytics();



const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

// Kick off the auth bootstrap (token-from-storage → /me) in parallel with
// React's first paint. The router shows a neutral "Loading…" state while
// `isBootstrapping` is true, so we never render protected routes against
// stale auth state.
void useAuthStore.getState().bootstrap();

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* basename comes from Vite's `base` (import.meta.env.BASE_URL) so the
          same source works at root in dev and under /app-next (or /app) in
          production. Trailing slash stripped — React Router wants none. */}
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
        {/* Last line of defence: without this a single render throw
            unmounts the whole tree and leaves a blank window with no way
            out, which on native reads as a dead app. */}
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
